import {useRef, Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {cn, MainLoader} from '@chekin/guestapp-ui-kit';
import {AlertBox, AlertSize, AlertTypes, Button} from '@chekinapp/ui';
import {useGuest, useGuestGroup, useIVComputedDetails} from 'hooks';
import {useRemoteIVFlow} from 'hooks/iv/ivRemoteFlow';
import {useSummary} from 'context/summary';
import {useGetGuestId} from '../../../../hooks/useGetGuestId';
import {useReservation} from 'context/reservation';
import {useGetVerificationSteps} from './useGetVerificationSteps';
import {useVerificationSettings} from 'hooks/iv/useVerificationSettings';
import {COUNTRY_CODES, IV_PROCESS_TYPE, SDK_MODE} from '@chekin/core';
import {ANALYTIC_EVENTS, Analytics} from '@chekin/shared';
import ChekinProSettings from '../../../../ChekinProSettings';
import VerticalLineIcon from '../../../../assets/icons/vertical-line.svg?react';
import {SkipIVModal, SkipIVModalRef} from 'components/common/SkipIVModal';
import {VerificationStepItem} from './VerificationStepItem';
import {Wrapper, Text, InfoBox, VerificationSteps} from './styled';
import {ModalTwoButtonsWrapper} from '@guestapp/old-ui-kit';

function StartView() {
  const {t} = useTranslation();
  const id = useGetGuestId();
  const {isLoading} = useGuest({id});

  const {biomatch, isDataLoaded} = useIVComputedDetails();

  const skipIVModalRef = useRef<SkipIVModalRef>(null);
  const openSkipModal = () => skipIVModalRef.current?.open();
  const {countryCode} = useSummary();
  const {housing} = useReservation();
  const {
    isEmailEnabled,
    isPhoneEnabled,
    isLoading: isSettingsLoading,
    isNeedVerification,
  } = useVerificationSettings(housing?.id);
  const {guestGroup} = useGuestGroup();

  const isExpectedLeaderGuest = !guestGroup?.added_number_of_guests;
  const isAgeInformationVisible =
    countryCode && countryCode !== COUNTRY_CODES.uae && !isExpectedLeaderGuest;

  const {startVerification} = useRemoteIVFlow(isNeedVerification);

  const getDescriptionItems = useGetVerificationSteps({
    biomatch,
    isLivenessDetectionEnabled: housing?.is_liveness_detection_enabled,
    isEmailEnabled,
    isPhoneEnabled,
    isNeedContactsVerification: isNeedVerification,
  });

  return (
    <Wrapper className="iv-start-view">
      <Text className="iv-start-view__text">{t('verify_you_identity_to_continue')}</Text>
      <InfoBox className="iv-start-view__info-box">
        {isLoading || isSettingsLoading || !isDataLoaded ? (
          <MainLoader visible width={60} />
        ) : (
          <VerificationSteps>
            {getDescriptionItems.map((step, index, arr) => (
              <Fragment key={`step-${index}`}>
                <VerificationStepItem icon={step.icon} text={t(step.text)} />

                {index < arr.length - 1 && (
                  <VerificationStepItem icon={<VerticalLineIcon />} />
                )}
              </Fragment>
            ))}
          </VerificationSteps>
        )}
      </InfoBox>

      {isAgeInformationVisible && (
        <AlertBox
          className="text-[var(--primary)] max-w-[400px] mb-6"
          type={AlertTypes.INFO}
          size={AlertSize.S}
        >
          {t('minors_under_18_age')}
        </AlertBox>
      )}

      <ModalTwoButtonsWrapper className="iv-start-view__buttons">
        <Button
          onClick={() => {
            void startVerification();
            Analytics.track(ANALYTIC_EVENTS.ivStarted, {
              type: biomatch ? 'Biomatch' : 'OCR',
            });
          }}
          disabled={isLoading}
        >
          {t('start_verification')}
        </Button>
        {ChekinProSettings?.mode !== SDK_MODE.onlyIV &&
          housing?.identity_verification_process_type !== IV_PROCESS_TYPE.onProperty && (
            <Button
              onClick={openSkipModal}
              disabled={isLoading}
              className={cn('kiosko:mx-auto kiosko:mt-4')}
              variant="link"
            >
              {t('skip_process')}
            </Button>
          )}
      </ModalTwoButtonsWrapper>
      <SkipIVModal ref={skipIVModalRef} />
    </Wrapper>
  );
}

export {StartView};
